/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Typography,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    useMediaQuery,
    styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useTheme } from '@mui/material/styles';
import SurfaceSection from "../Common/SurfaceSection";
import CircularProgress from '@mui/material/CircularProgress';
import { ClientTypesMigrationReport } from "../../types/Reports";
import useReportsService from "../../services/useReportsService";
import ReportsPieChart from "./ClientTypesReportDialog/ReportPieChart";
import { ClientTypesDescriptionTranslationKeys } from "../../constants/reports/ClientTypes";
import { formatNumber } from "../../utils/formatNumber";
import DatePickers from "../Common/Charts/DatePickers/DatePickers";
import { setStartDayHours, setStartOfNextDay } from "../../utils/time";

// Two adjacent, calendar-aligned 3-month blocks that don't overlap and have the same shape. Because an
// interval's end date is expanded to the next midnight when queried (it includes the whole end day), each
// block is displayed as [start .. day before the next block's start]:
//   Interval 1: [6 months ago .. day before 3 months ago]   -> block [-6m, -3m)
//   Interval 2: [3 months ago .. yesterday]                  -> block [-3m, today)
// Interval 2 ends yesterday (not today) so it stays a complete block symmetric with interval 1 and excludes
// the partial current day.
const kBoundary = new Date(new Date().setMonth(new Date().getMonth() - 3));         // 3 months ago (divider)
const kDayBeforeBoundary = new Date(new Date(kBoundary).setDate(kBoundary.getDate() - 1));
const kYesterday = new Date(new Date().setDate(new Date().getDate() - 1));

const DefaultFilters = {
    secondIntervalStartDate: kBoundary.toISOString().slice(0, 10),
    secondIntervalEndDate: kYesterday.toISOString().slice(0, 10),
    firstIntervalStartDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().slice(0, 10),
    firstIntervalEndDate: kDayBeforeBoundary.toISOString().slice(0, 10),
}

type DateFilter = {
    firstIntervalStartDate: number | string;
    firstIntervalEndDate: number | string;
    secondIntervalStartDate: number | string;
    secondIntervalEndDate: number | string;
};

const CloseButton = styled(IconButton)(({ theme }) => `
    color: ${theme.palette.primary[100]};
    position: absolute;
    right: ${theme.spacing(2)};
    top: ${theme.spacing(2)};
`);

const DatePickersContainer = styled(Box)(({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(3)};
`);

const DateIntervalContainer = styled(Box)(({ theme }) => `
    align-items: start;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
`);

const DescriptionText = styled('span')(({ theme }) => `
    color: ${theme.palette.text.secondary};
    display: block;
    font-size: 0.95em;
    white-space: nowrap;
`);

function ClientTypesReportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
    const [clientTypesMigrationReport, setClientTypesMigrationReport] = useState<ClientTypesMigrationReport>({ reportData1: [], reportData2: [] });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pieWidth, setPieWidth] = useState(400);
    const [dateFilter, setDateFilter] = useState<DateFilter>(DefaultFilters);
    const pieParentRef = useRef<HTMLDivElement>(null);

    const theme = useTheme();
    const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { t } = useTranslation("common");
    const { fetchClientTypesMigrationReportData } = useReportsService();

    useEffect(() => {
        if (!open) return;

        setIsLoading(true);
        fetchClientTypesMigrationReportData(
            setStartDayHours(dateFilter.firstIntervalStartDate),
            setStartOfNextDay(dateFilter.firstIntervalEndDate),
            setStartDayHours(dateFilter.secondIntervalStartDate),
            setStartOfNextDay(dateFilter.secondIntervalEndDate),
        ).then((data) => {
            setClientTypesMigrationReport(data);
            setError(null);
        }).catch(() => {
            setError(t("reports.common.errorFetchingData"));
        }).finally(() => {
            setIsLoading(false);
        });
    }, [dateFilter, open, fetchClientTypesMigrationReportData, t]);

    // todo this should be in the pie chart component
    // adjust pie width based on parent container
    useEffect(() => {
        const handleResize = () => {
            if (pieParentRef.current) {
                const parentWidth = pieParentRef.current.offsetWidth;
                if (isTabletOrMobile) {
                    setPieWidth(parentWidth - 40);
                } else {
                    setPieWidth((parentWidth - 170) / 2);
                }
            }
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [pieParentRef.current?.offsetWidth, pieParentRef.current?.offsetHeight, isTabletOrMobile]);

    // todo this should be in the pie chart component
    // cleanup on close
    useEffect(() => {
        return () => {
            setDateFilter(DefaultFilters);
            setPieWidth(400);
        };
    }, []);
    // todo maybe we can have some constants for client types and map over them
    // prepare table data
    const tableData = useMemo(
        () => ([
            {
                label: t("reports.clientTypesMigration.rareClients"),
                description: t(ClientTypesDescriptionTranslationKeys.RARE),
                firstIntervalValue: formatNumber(clientTypesMigrationReport.reportData1.find(c => c.name === 'Clienti rari')?.customers || 0),
                secondIntervalValue: formatNumber(clientTypesMigrationReport.reportData2.find(c => c.name === 'Clienti rari')?.customers || 0),
            },
            {
                label: t("reports.clientTypesMigration.occasionalClients"),
                description: t(ClientTypesDescriptionTranslationKeys.OCCASIONAL),
                firstIntervalValue: formatNumber(clientTypesMigrationReport.reportData1.find(c => c.name === 'Clienti ocazionali')?.customers || 0),
                secondIntervalValue: formatNumber(clientTypesMigrationReport.reportData2.find(c => c.name === 'Clienti ocazionali')?.customers || 0),
            },
            {
                label: t("reports.clientTypesMigration.loyalClients"),
                description: t(ClientTypesDescriptionTranslationKeys.LOYAL),
                firstIntervalValue: formatNumber(clientTypesMigrationReport.reportData1.find(c => c.name === 'Clienti fideli')?.customers || 0),
                secondIntervalValue: formatNumber(clientTypesMigrationReport.reportData2.find(c => c.name === 'Clienti fideli')?.customers || 0),
            },
            {
                label: t("reports.clientTypesMigration.fanClients"),
                description: t(ClientTypesDescriptionTranslationKeys.FAN),
                firstIntervalValue: formatNumber(clientTypesMigrationReport.reportData1.find(c => c.name === 'Clienti inraiti')?.customers || 0),
                secondIntervalValue: formatNumber(clientTypesMigrationReport.reportData2.find(c => c.name === 'Clienti inraiti')?.customers || 0),
            },
        ]), [t, clientTypesMigrationReport]
    );

    // prepare chart data
    const totalClientsFirstInterval = useMemo(
        () => (clientTypesMigrationReport.reportData1 || []).reduce((acc, curr) => acc + (curr.customers || 0), 0),
        [clientTypesMigrationReport]
    );

    const totalClientsSecondInterval = useMemo(
        () => (clientTypesMigrationReport.reportData2 || []).reduce((acc, curr) => acc + (curr.customers || 0), 0),
        [clientTypesMigrationReport]
    );

    const firstIntervalChartData = useMemo(
        () =>
            (clientTypesMigrationReport.reportData1 && clientTypesMigrationReport.reportData1.length > 0)
                ? clientTypesMigrationReport.reportData1.map(c => ({
                    label: c.name,
                    value: c.customers || 0
                }))
                : [],
        [clientTypesMigrationReport]
    );

    const secondIntervalChartData = useMemo(
        () =>
            (clientTypesMigrationReport.reportData2 && clientTypesMigrationReport.reportData2.length > 0)
                ? clientTypesMigrationReport.reportData2.map(c => ({
                    label: c.name,
                    value: c.customers || 0
                }))
                : [],
        [clientTypesMigrationReport]
    );

    const noData = useMemo(() => (
        !totalClientsFirstInterval || !totalClientsSecondInterval
    ), [totalClientsFirstInterval, totalClientsSecondInterval]);


    const renderContent = () => {
        if (error) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <Typography variant="h6" align="center" width="100%" color="error">
                        {error}
                    </Typography>
                </Box>
            );
        }

        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <CircularProgress />
                </Box>
            );
        }

        if (noData) {
            return (
                <Box display="flex" justifyContent="center" my={2}>
                    <Typography variant="h6" align="center" width="100%">
                        {t("reports.common.noData")}
                    </Typography>
                </Box>
            );
        }

        // todo this should be more generic
        return (
            <ReportsPieChart
                pieWidth={pieWidth}
                data={{
                    uniqueClientsChartData: {
                        data: firstIntervalChartData,
                        totalValue: totalClientsFirstInterval,
                        title: t("reports.clientTypesMigration.nrOfuniqueClients.firstInterval"),
                    },
                    totalVisitsChartData: {
                        data: secondIntervalChartData,
                        totalValue: totalClientsSecondInterval,
                        title: t("reports.clientTypesMigration.nrOfuniqueClients.secondInterval"),
                    }
                }
                }
            />
        )
    };

    const renderTable = () => {
        if (isLoading || noData) return null;
        return (
            <Box>
                <Table size="small" sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left" sx={{ fontWeight: "bold" }}>{t("reports.clientTypesMigration.clientType")}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>{t("reports.clientTypesMigration.uniqueClients.firstInterval")}</TableCell>
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>{t("reports.clientTypesMigration.uniqueClients.secondInterval")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableData.map((row) => (
                            <TableRow key={row.label}>
                                <TableCell>
                                    <span>{row.label}</span>
                                    <DescriptionText>{row.description}</DescriptionText>
                                </TableCell>
                                <TableCell align="center">{row.firstIntervalValue}</TableCell>
                                <TableCell align="center">{row.secondIntervalValue}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        )
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <DialogTitle>
                {t("reports.clientTypesMigration.title")}
                <CloseButton aria-label="close" onClick={onClose}>
                    <CloseIcon />
                </CloseButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}>
                <Box sx={{ mb: 2 }}>
                    <Typography>
                        {t("reports.clientTypesMigration.subtitle")}
                    </Typography>
                </Box>
                <Box
                    display="flex"
                    flex={1}
                    gap={2}
                    flexDirection={isTabletOrMobile ? "column" : "row"}
                >
                    <Box
                        flex={isTabletOrMobile ? 12 : 3}
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                    >
                        <DatePickersContainer>
                            <DateIntervalContainer>
                                <Typography variant="h6">
                                    {t("reports.clientTypesMigration.firstInterval")}
                                </Typography>
                                <DatePickers
                                    fromValue={dateFilter.firstIntervalStartDate}
                                    toValue={dateFilter.firstIntervalEndDate}
                                    onFromChange={(date) => setDateFilter({ ...dateFilter, firstIntervalStartDate: date?.valueOf() ?? 0 })}
                                    onToChange={(date) => setDateFilter({ ...dateFilter, firstIntervalEndDate: date?.valueOf() ?? 0 })}
                                />
                            </DateIntervalContainer>

                            <DateIntervalContainer>
                                <Typography variant="h6">
                                    {t("reports.clientTypesMigration.secondInterval")}
                                </Typography>
                                <DatePickers
                                    fromValue={dateFilter.secondIntervalStartDate}
                                    toValue={dateFilter.secondIntervalEndDate}
                                    onFromChange={(date) => setDateFilter({ ...dateFilter, secondIntervalStartDate: date?.valueOf() ?? 0 })}
                                    onToChange={(date) => setDateFilter({ ...dateFilter, secondIntervalEndDate: date?.valueOf() ?? 0 })}
                                />
                            </DateIntervalContainer>
                        </DatePickersContainer>
                        {!isTabletOrMobile && renderTable()}
                    </Box>

                    <SurfaceSection
                        ref={pieParentRef}
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flex: isTabletOrMobile ? 12 : 9,
                            flexDirection: isTabletOrMobile ? "column" : "row",
                            justifyContent: "center",
                            pb: isTabletOrMobile ? 2 : 0,
                        }}
                    >
                        {renderContent()}
                    </SurfaceSection>
                    {isTabletOrMobile && renderTable()}
                </Box>
            </DialogContent >
        </Dialog >
    );
};

export default ClientTypesReportDialog;
